import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import { RendererProps } from "../BaseRenderer";
import { translate } from "../../../models/utilities/translationUtilities";
import { getSettingString } from "../../../models/utilities/settingUtilities";
import useStyle from "./ImageAreaRenderer.styles";
import useQuestion from "../../../hooks/useQuestion";
import { QuestionType } from "../../../models/enums/QuestionType";
import useDimensionContext from "../../../hooks/useDimensionContext";
import { RecalledText } from "../../../containers/shared/RecalledText";
import { ChoiceModel } from "../../../models/ChoiceModel";

interface Props extends RendererProps {
    imageSrc: string;
    legendLabel: string;
    height: string;
    width: string;
    choices: ChoiceModel[];
}

function Legend(props: Props) {
    const theme = useTheme();
    const classes = useStyle({ theme });
    const { choices, legendLabel, language } = props;
    const dimension = useDimensionContext();

    const legendItems = Object.keys(choices).map((key) => {
        const choice: ChoiceModel = choices[key];

        return (
            <div key={key} className={classes.imageAreaLegendContainer}>
                <div
                    id={choice.elementId}
                    className={classes.imageAreaLegendColor}
                    style={{ background: getSettingString(choices[key], "Color") }}
                />
                <RecalledText
                    text={translate(choices[key], "TEXT", language)}
                    render={(recalled) => (
                        <div
                            className={classes.imageAreaLegendText}
                            dangerouslySetInnerHTML={{ __html: recalled }}
                        />
                    )}
                />
            </div>
        );
    });

    return (
        <>
            {dimension && dimension.maxWidth(670) ? (
                <div
                    className={classnames(classes.imageAreaLegend, classes.dynamicImageAreaLegend)}
                >
                    <div className={classnames(classes.imageAreaLegendTitle, classes.initialFlex)}>
                        Legend
                    </div>
                    <div className={classnames(classes.imageAreaLegendList, classes.initialFlex)}>
                        {legendItems}
                    </div>
                </div>
            ) : (
                <div className={classes.imageAreaLegend}>
                    <div className={classes.imageAreaLegendTitle}>{legendLabel}</div>
                    <div className={classes.imageAreaLegendList}>{legendItems}</div>
                </div>
            )}
        </>
    );
}

export default function ImageAreaRenderer(props: Props) {
    const theme = useTheme();
    const classes = useStyle({ theme });
    const { imageSrc, children, height, width } = props;
    const question = useQuestion();
    const displayLegend = question.type === QuestionType.ImageAreaEvaluator;

    return (
        <>
            <div className={classes.imageArea} role="group">
                <img
                    className={classes.imageAreaImage}
                    src={imageSrc}
                    alt=""
                    style={{ width, height }}
                />
                {children}
            </div>
            {displayLegend && <Legend {...props} />}
        </>
    );
}
